/* eslint-disable import/no-duplicates -- locales are not exported at the top level of date-fns */
import { formatRelative } from 'date-fns';

import enUS from 'date-fns/locale/en-US';

/**
 * Parses a date input (Date object or string) into a Date object.
 *
 * Handles date-only strings in YYYY-MM-DD format by parsing them in the local timezone
 * to avoid timezone shifts.
 *
 * @param date - The date to parse into a Date object. Can be a Date object or
 * a string that can be parsed into a Date object.
 * @returns A Date object
 */
export const parseDate = (date: Date | string): Date => {
  if (date instanceof Date) {
    return date;
  }

  // The input is a string, so we need to parse it. If the string is in
  // the format YYYY-MM-DD, treat it as a date-only string and parse it as that
  // day in the local timezone.
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-');
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  // Not a date-only string, so parse it normally as a datetime string.
  return new Date(date);
};

/**
 * Returns a date in DateOnly (YYYY-MM-DD) format for API payloads.
 * Use this when the backend expects a date-only string; do not use formatDate (locale string).
 *
 * @param date - The date to format.
 * @returns YYYY-MM-DD or undefined if date is null/undefined/invalid.
 */
export function toDateOnlyString(date: Date | string | null | undefined): string | undefined {
  if (!date) return undefined;
  try {
    const d = parseDate(date);
    if (Number.isNaN(d.getTime())) return undefined;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  } catch {
    return undefined;
  }
}

/**
 * Checks if a date string, converted to date, is valid.
 *
 * @param dateString - The date string to check.
 * @returns True if the date string is valid, false otherwise.
 */
export function isDateValid(dateString: string): boolean {
  return !Number.isNaN(parseDate(dateString).getTime());
}

/**
 * Formats a date into a locale-appropriate date string.
 *
 * @param date - The date to format as a Date object or a string that can be parsed into a Date object.
 * @param shouldIncludeTime - Whether to include the time in the formatted date string.
 * @returns The formatted date string.
 */
export const formatDate = (date: Date | string | null | undefined, shouldIncludeTime = false) => {
  if (!date) return '';

  return parseDate(date).toLocaleDateString(navigator.language, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(shouldIncludeTime && { hour: 'numeric', minute: 'numeric' }),
  });
};

/**
 * Formats a date into a relative (today, yesterday, last week, etc) date
 * string. Dates further in the past are formatted as the date
 * in a locale-appropriate format.
 *
 * @param date - The date string to format as a Date object or a string that can be parsed into a Date object.
 * @param shouldIncludeTime - Whether to include the time in the formatted date
 * string.
 * @returns The formatted date string.
 */
export const formatRelativeDate = (
  date: Date | string | null | undefined,
  shouldIncludeTime = false
) => {
  if (!date) return '';

  const formats = {
    lastWeek: `'Last' eeee${shouldIncludeTime ? `' at' p` : ''}`,
    nextWeek: `eeee${shouldIncludeTime ? `' at' p` : ''}`,
    today: `'Today${shouldIncludeTime ? ` at' p` : "'"}`,
    tomorrow: `'Tomorrow${shouldIncludeTime ? ` at' p` : "'"}`,
    yesterday: `'Yesterday${shouldIncludeTime ? ` at' p` : "'"}`,
    other: `'${formatDate(date, shouldIncludeTime)}'`,
  };

  return formatRelative(parseDate(date), new Date(), {
    locale: {
      ...enUS,
      formatRelative: (token: keyof typeof formats) => formats[token] || formats.other,
    },
  });
};

/**
 * Formats a date into a locale-appropriate string containing only the month and year.
 *
 * @param date - The date to format as a Date object or a string that can be parsed into a Date object.
 * @returns The formatted date string.
 */
export const formatMonthYearDate = (date: Date | string) => {
  return parseDate(date).toLocaleDateString(navigator.language, {
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Formats a date as time if it's today, or as a date if it's not today.
 *
 * @param date - The date to format as a Date object or a string that can be parsed into a Date object.
 * @returns The formatted time string (if today) or date string (if not today).
 */
export const formatTimeOrDate = (date: Date | string) => {
  const inputDate = parseDate(date);
  const today = new Date();

  const isToday = inputDate.toDateString() === today.toDateString();

  if (isToday) {
    return inputDate.toLocaleTimeString(navigator.language, {
      hour: 'numeric',
      minute: 'numeric',
    });
  }

  return formatDate(inputDate);
};

const FORMATTED_DATESTRING = formatDate(new Date('3333-11-22T12:00:00'));

/**
 * Empty date string for when no date is set. Eg, "--/--/----"
 */
export const EMPTY_DATE = FORMATTED_DATESTRING.replaceAll('3', '-')
  .replaceAll('1', '-')
  .replaceAll('2', '-');

/**
 * JS ECMA ISO 8601 Date Format. Eg, "2025-01-31"
 *
 * @see https://tc39.es/ecma262/multipage/numbers-and-dates.html#sec-date-time-string-format
 */
export const DATE_FORMAT_MASK = FORMATTED_DATESTRING.replaceAll('3', 'Y')
  .replaceAll('1', 'M')
  .replaceAll('2', 'D');

/**
 * Pre-process the date to handle GMT UTC so that we don't slide to yesterday.
 *
 * TODO: this is a hack and we should find a better way to handle this. See
 * Dan's recent work on untangling the mess of Item due dates to see the trouble
 * this kind of hack can get us into.
 *
 * TODO: Like the above says, this is a hack. If we need one for the time being,
 * we should strive not to export hacks at least.
 *
 * @deprecated
 *
 */
export function preProcessDay(date: Date): Date {
  const isDayOnly = date.getUTCHours() === 0 && date.getUTCMinutes() === 0;
  // handle GMT UTC so that we don't slide to yesterday...
  if (isDayOnly) date.setUTCMinutes(date.getTimezoneOffset() + 1);
  return date;
}

/**
 * Preprocess the date to handle GMT UTC so that we don't slide to yesterday.
 * Then format the date as a locale-formatted date string, or null if the input
 * is invalid.
 *
 * @deprecated this is a hack to workaround old date pickers. Use Scales
 * DateInput to select dates and formatDate (or other format*** functions) to
 * format them.
 */
export function adjustAndFormatDate(date: Date | string | undefined | null): string | null {
  if (!date) return null;

  // date strings can arrive in UTC Z +8, adjust if need-be
  return formatDate(preProcessDay(new Date(date)));
}

/**
 * NB, the below functions (setAllDayRange, todayUTCNoonDate) are pretty
 * one-off-y or sketchy. Consider whether they're patterns we want to continue
 * using and encourage.
 *
 * Eg, `todayUTCNoonDate` is a bit of a hack and a suggestion that maybe we
 * shouldn't be using a `Date` (which is a date-time) to represent this value -
 * perhaps it should be a time-less date.
 */

/**
 * Sets the range for start day and end day dates as maximum as possible
 *
 * @param [start, end]: Date[]
 * @returns [start, end]: Date[]
 * @deprecated
 */
export function setAllDayRange([start, end]: Date[]): [Date, Date] {
  start.setUTCHours(0, 0, 0, 0);
  end.setUTCHours(23, 59, 59, 999);
  return [start, end] as const;
}

/**
 * Gets today at UTC noon Date
 * For the time difference comparison we are a single day precise - yesterday, today or tomorrow
 *
 * @returns today: Date
 * @deprecated
 */
export function todayUTCNoonDate(): Date {
  const today = new Date(Date.now());
  today.setUTCHours(12, 0, 0, 0);
  return today;
}

/**
 * NB, the below functions are deprecated and only used by MUI DateInput.
 * Do not use them in any new code.
 */

/**
 * @deprecated only used by MUI DateInput
 */
const MASK = FORMATTED_DATESTRING
  ? FORMATTED_DATESTRING.split('').map((char) => {
      if (['/', '-', '.'].includes(char)) return char;
      return /\d/;
    })
  : [];

/**
 * @deprecated only used by MUI DateInput
 */
export const maskFn = (value: string) => (value ? MASK : []);

/**
 * @deprecated unicode date format used only for MUI DateInput
 * https://cldr.unicode.org/translation/date-time/date-time-patterns#basic-date-formats
 */
export const localeDateMaskMUI = FORMATTED_DATESTRING.replaceAll('3', 'y')
  .replaceAll('1', 'M')
  .replaceAll('2', 'd');
